import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Key, Smartphone, Shield, Lock, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function MFAPasskeys() {
  const breadcrumbs = [{
    label: 'Tools Library',
    path: '/tools'
  }, {
    label: 'MFA & Passkeys'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-5070">
      <div className="container mx-auto px-4 py-12" data-id="element-5071">
        {/* Hero */}
        <div className="mb-12" data-id="element-5072">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-500/10 text-teal-500 text-xs font-medium mb-4 border border-teal-500/20" data-id="element-5073">
            <Key className="h-3 w-3 mr-2" data-id="element-5074" />
            Tools Library
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-5075">
            MFA & Passkeys
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-5076">
            Multi-Factor Authentication and Passkeys are your strongest defense
            against account takeover. Passwords alone are no longer enough.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-5077">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-5078">
            {/* Why This Matters */}
            <section data-id="element-5079">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-5080">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-teal-500 p-6 rounded-r-lg" data-id="element-5081">
                <p className="text-slate-300 mb-4" data-id="element-5082">
                  Passwords are easily stolen, phished, or guessed. MFA adds a
                  second layer of defense, making it significantly harder for
                  attackers to access your accounts even if they have your
                  password.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-5083">
                  <li className="flex items-start" data-id="element-5084">
                    <span className="text-teal-500 mr-2" data-id="element-5085">•</span> Passkeys
                    eliminate passwords entirely and are phishing-resistant
                  </li>
                  <li className="flex items-start" data-id="element-5086">
                    <span className="text-teal-500 mr-2" data-id="element-5087">•</span> MFA is
                    required by most modern compliance frameworks
                  </li>
                  <li className="flex items-start" data-id="element-5088">
                    <span className="text-teal-500 mr-2" data-id="element-5089">•</span> Hardware keys
                    offer the highest level of security available
                  </li>
                </ul>
              </div>
            </section>

            {/* Tool Categories */}
            <section data-id="element-5090">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5091">
                Recommended Tools
              </h2>
              <div className="grid gap-6" data-id="element-5092">
                <ToolCategory title="Authenticator Apps (TOTP)" icon={Smartphone} desc="Time-based codes generated on your phone. Works offline and is more secure than SMS." tools={[{
                name: 'Authy',
                note: 'Cloud backup, multi-device support'
              }, {
                name: 'Google Authenticator',
                note: 'Simple, widely supported'
              }, {
                name: '1Password',
                note: 'Integrated TOTP, convenient'
              }]} safety="Enable cloud backups or save recovery codes to avoid lockout if phone is lost." data-id="element-5093" />
                <ToolCategory title="Hardware Security Keys" icon={Key} desc="Physical devices that you plug in or tap. The gold standard for security." tools={[{
                name: 'YubiKey',
                note: 'Industry standard, durable'
              }, {
                name: 'Titan Security Key',
                note: "Google's hardware key"
              }, {
                name: 'Nitrokey',
                note: 'Open source hardware'
              }]} safety="Always buy two keys (primary + backup) and register both." data-id="element-5094" />
                <ToolCategory title="Passkeys (WebAuthn)" icon={Shield} desc="Cryptographic keys stored on your device. No passwords to remember or type." tools={[{
                name: 'Apple Passkeys',
                note: 'Integrated into iOS/macOS'
              }, {
                name: 'Google Passkeys',
                note: 'Integrated into Android/Chrome'
              }, {
                name: '1Password',
                note: 'Cross-platform passkey support'
              }]} safety="Ensure you have a recovery method for your ecosystem account (Apple ID / Google Account)." data-id="element-5095" />
              </div>
            </section>

            {/* Implementation Guide */}
            <section data-id="element-5096">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5097">
                Implementation Guide
              </h2>
              <div className="space-y-4" data-id="element-5098">
                <Step number={1} text="Download an authenticator app (Authy recommended for beginners)." data-id="element-5099" />
                <Step number={2} text="Enable MFA on critical accounts (Email, Banking, Social Media)." data-id="element-5100" />
                <Step number={3} text="Save recovery codes in a secure location (Password Manager or offline)." data-id="element-5101" />
                <Step number={4} text="Consider buying hardware keys for high-value accounts (Admin access, Crypto)." data-id="element-5102" />
                <Step number={5} text="Start using Passkeys where supported (Google, GitHub, PayPal)." data-id="element-5103" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-5104">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5105">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-5106">
                <ul className="space-y-3 text-slate-300" data-id="element-5107">
                  <li className="flex items-start" data-id="element-5108">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5109" />
                    Using SMS as primary MFA (vulnerable to SIM swapping).
                  </li>
                  <li className="flex items-start" data-id="element-5110">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5111" />
                    Not saving recovery codes (getting locked out permanently).
                  </li>
                  <li className="flex items-start" data-id="element-5112">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5113" />
                    Only registering one hardware key (no backup if lost).
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-5114">
            <Card className="p-6 sticky top-24" data-id="element-5115">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-5116">
                Quick Actions
              </h3>
              <Link to="/personal/identity" className="block w-full" data-id="element-5117">
                <Button className="w-full bg-teal-500 hover:bg-teal-600 text-slate-950" data-id="element-5118">
                  Identity Protocol <ArrowRight className="ml-2 h-4 w-4" data-id="element-5119" />
                </Button>
              </Link>
              <Link to="/tools/password-safety" className="block w-full mt-3" data-id="element-5120">
                <Button variant="secondary" className="w-full" data-id="element-5121">
                  Password Safety Check
                </Button>
              </Link>

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-5122">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-5123">
                  Related
                </h4>
                <ul className="space-y-2 text-sm text-slate-400" data-id="element-5124">
                  <li data-id="element-5125">
                    <Link to="/personal/communication" className="hover:text-teal-500" data-id="element-5126">
                      Secure Communication
                    </Link>
                  </li>
                  <li data-id="element-5127">
                    <Link to="/organization/protect" className="hover:text-teal-500" data-id="element-5128">
                      Organization Protection
                    </Link>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ToolCategory({
  title,
  icon: Icon,
  desc,
  tools,
  safety
}: {
  title: string;
  icon: any;
  desc: string;
  tools: {
    name: string;
    note: string;
  }[];
  safety: string;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800" data-id="element-5129">
      <div className="flex items-start mb-4" data-id="element-5130">
        <div className="bg-teal-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-5131">
          <Icon className="h-6 w-6 text-teal-500" data-id="element-5132" />
        </div>
        <div data-id="element-5133">
          <h3 className="text-lg font-bold text-slate-200 mb-1" data-id="element-5134">{title}</h3>
          <p className="text-slate-400 text-sm" data-id="element-5135">{desc}</p>
        </div>
      </div>

      <div className="space-y-3 mb-6 pl-16" data-id="element-5136">
        {tools.map((tool, idx) => <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 last:border-0 last:pb-0" data-id="element-5137">
            <span className="font-bold text-slate-300" data-id="element-5138">{tool.name}</span>
            <span className="text-slate-500" data-id="element-5139">{tool.note}</span>
          </div>)}
      </div>

      <div className="bg-slate-800/50 p-3 rounded text-xs text-slate-400 flex items-start" data-id="element-5140">
        <Shield className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" data-id="element-5141" />
        <span data-id="element-5142">
          <strong data-id="element-5143">Safety Note:</strong> {safety}
        </span>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-5144">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-teal-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-5145">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-5146">{text}</p>
    </div>;
}